import {
  CALLSITE_LOGGER,
  LOGGER_PROXIED,
  setCurrentCallsiteLogger,
} from "@-/com.npmjs.drizzle-orm/expo-sqlite-async/logger-context";
import { isThenable } from "@-/com.npmjs.is-thenable";

// Threads a per-callsite logger (e.g. `logger(import.meta.location)`) onto every drizzle
// query object built through this handle, so the vendored prepared query can
// emit its timing log with the ORIGINAL call site's `[file:line:col]` prefix
// rather than the logger module's own location.
//
// `db(logger).query.X.findFirst(...)` builds the query object once; the same
// object may be re-run on every change-listener event. So the logger
// can't be stashed transiently — it is STAMPED on the built query object
// (`CALLSITE_LOGGER`), and the query's `then`/`execute` push it into a
// synchronous slot read by `ExpoSQLiteAsyncSession.prepareQuery`.
//
// Chained builders (`update(t).set(v).where(c)`, `insert(t).values(v)`) and
// nested handles (`query.X`, transaction `tx`, `softDelete(t)`) return further
// objects, so the proxy re-wraps every non-terminal result and re-stamps every
// terminal (thenable) query — surviving being passed whole into helpers.

function isObjectLike(value: unknown): value is object {
  return (typeof value === "object" || typeof value === "function") && value !== null;
}

function isAsyncDatabaseGetter<Database extends object>(
  value: Database | (() => Promise<Database>),
): value is () => Promise<Database> {
  return typeof value === "function";
}

function defineHidden(target: object, key: PropertyKey, value: unknown): void {
  Object.defineProperty(target, key, {
    configurable: true,
    enumerable: false,
    value,
    writable: true,
  });
}

// Wrap `then`/`execute` on a built query object so executing it pushes the
// callsite logger into the slot for the synchronous `_prepare → prepareQuery`
// window that follows.
function stampThenable(query: PromiseLike<unknown>, logger: Console): PromiseLike<unknown> {
  if (Reflect.get(query, LOGGER_PROXIED) === true) {
    return query;
  }
  defineHidden(query, LOGGER_PROXIED, true);
  defineHidden(query, CALLSITE_LOGGER, logger);

  // `then` / `execute` cover `await query` and `query.execute()`. `run` / `all`
  // / `get` / `values` are the terminal methods drizzle exposes directly on
  // `SQLiteInsertBase` / `SQLiteUpdateBase` / `SQLiteDeleteBase` and on
  // relational queries — calling them bypasses `then`/`execute` and so would
  // bypass the logger threading without their own stamping.
  for (const method of ["then", "execute", "run", "all", "get", "values"] as const) {
    const original = Reflect.get(query, method);
    if (typeof original !== "function") {
      continue;
    }
    defineHidden(query, method, (...args: unknown[]) => {
      setCurrentCallsiteLogger(logger);
      return Reflect.apply(original, query, args);
    });
  }

  return query;
}

function wrapTransactionCallback(callback: unknown, logger: Console): unknown {
  if (typeof callback !== "function") {
    return callback;
  }
  return (tx: unknown) =>
    Reflect.apply(callback, undefined, [isObjectLike(tx) ? wrap(tx, logger) : tx]);
}

function wrapResult(result: unknown, logger: Console): unknown {
  if (isThenable(result)) {
    // A terminal query (relational query / select / insert / update).
    return stampThenable(result, logger);
  }
  if (isObjectLike(result)) {
    // A builder or nested handle whose further calls must re-stamp.
    return wrap(result, logger);
  }
  return result;
}

// Wrap a single method so its result is re-wrapped/stamped. `isTransaction`
// rewrites the user callback so its `tx` argument is itself a logger proxy and
// the returned promise is left untouched (its queries were already stamped).
function wrapMethod(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- a drizzle method of any arity; invoked via Reflect.apply.
  fn: Function,
  receiver: unknown,
  logger: Console,
  isTransaction: boolean,
): (...args: unknown[]) => unknown {
  return (...args: unknown[]) => {
    if (isTransaction) {
      return Reflect.apply(fn, receiver, [
        wrapTransactionCallback(args[0], logger),
        ...args.slice(1),
      ]);
    }
    return wrapResult(Reflect.apply(fn, receiver, args), logger);
  };
}

function wrap<Target extends object>(target: Target, logger: Console): Target {
  return new Proxy(target, {
    apply(fnTarget, thisArg, argList) {
      if (typeof fnTarget !== "function") {
        throw new TypeError("Cannot call a non-function database handle");
      }
      return wrapResult(Reflect.apply(fnTarget, thisArg, argList), logger);
    },
    get(objTarget, property, receiver) {
      const value = Reflect.get(objTarget, property, receiver);
      if (typeof value === "function") {
        return wrapMethod(value, objTarget, logger, property === "transaction");
      }
      if (isObjectLike(value)) {
        return wrap(value, logger);
      }
      return value;
    },
  });
}

// Records a single step of property access / call along the chain so it can be
// replayed against the resolved database. We cannot use `proxymise` because
// drizzle's terminal builders (`SQLiteInsertBase` post-`.values()`,
// `SQLiteUpdateBase` post-`.set()`, relational `findFirst`/`findMany`) are
// themselves thenable — proxymise eagerly chains `.then` on every intermediate
// thenable, which causes `.values()` to auto-execute the query and the
// subsequent `.run()` to be invoked on the execution result instead of the
// builder.
type ChainStep = { kind: "get"; property: PropertyKey } | { args: unknown[]; kind: "apply" };

function replayChain(initial: unknown, steps: readonly ChainStep[]): unknown {
  let current: unknown = initial;
  let getReceiver: unknown = undefined;
  for (const step of steps) {
    if (step.kind === "get") {
      if (!isObjectLike(current)) {
        throw new TypeError(
          `Cannot read property "${String(step.property)}" of ${current === null ? "null" : typeof current}`,
        );
      }
      getReceiver = current;
      current = Reflect.get(current, step.property);
    } else {
      if (typeof current !== "function") {
        throw new TypeError("Attempted to call a non-function value on an async database chain");
      }
      current = Reflect.apply(current, getReceiver, step.args);
      getReceiver = undefined;
    }
  }
  return current;
}

function makeLazyAsyncChain<Database extends object>(
  resolveDb: () => Promise<Database>,
  steps: readonly ChainStep[],
): Database {
  const append = (step: ChainStep): Database => makeLazyAsyncChain(resolveDb, [...steps, step]);

  const execute = async (): Promise<unknown> => {
    const resolved = await resolveDb();
    const result = replayChain(resolved, steps);
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- drizzle chain terminus may be thenable
    return await (result as PromiseLike<unknown>);
  };

  // The proxy target is a function so both `get` and `apply` traps fire — drizzle
  // chains alternate between property access and call.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- empty function stand-in for Database proxy target
  const target = function () {} as unknown as object;
  /* eslint-disable typescript/no-unsafe-type-assertion -- proxy target is a typed stand-in for Database */
  return new Proxy(target, {
    apply(_target, _thisArg, argList) {
      return append({ args: argList, kind: "apply" });
    },
    get(_target, property) {
      if (property === "then") {
        const promise = execute();
        return promise.then.bind(promise);
      }
      if (property === "catch") {
        const promise = execute();
        return promise.catch.bind(promise);
      }
      if (property === "finally") {
        const promise = execute();
        return promise.finally.bind(promise);
      }
      return append({ kind: "get", property });
    },
  }) as Database;
  /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */
}

export function withDatabaseLoggerProxy<Database extends object>(
  db: Database | (() => Promise<Database>),
  logger: Console | null,
): Database {
  if (isAsyncDatabaseGetter(db)) {
    return makeLazyAsyncChain<Database>(
      async () => (logger === null ? await db() : wrap(await db(), logger)),
      [],
    );
  }

  // A `null` logger means "don't thread / don't log" (e.g. tests pass `db(null)`),
  // so hand back the raw handle untouched — no proxy, no per-query log line.
  if (logger === null) {
    return db;
  }

  return wrap(db, logger);
}
